import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArrowLeft, Trash } from "@phosphor-icons/react";

const STATUS_LABELS = {
  neu: "Neue Buchung", fotos_pruefen: "Fotos prüfen", bestaetigt: "Bestätigt",
  angenommen: "Fahrzeug angenommen", in_arbeit: "In Arbeit", trocknung: "Trocknung / Aushärtung",
  montage: "Montage", fertig_zur_abholung: "Fertig zur Abholung", abgeschlossen: "Abgeschlossen", storniert: "Storniert",
};
const HIMMEL_LABELS = {
  kleinwagen: "Kleinwagen", limousine: "Limousine", coupe: "Coupé", kombi: "Kombi",
  suv: "SUV", van: "Van / Transporter", sonstiges: "Sonstiges",
};
const HECK_LABELS = { klein: "Klein (bis 20 cm)", mittel: "20–50 cm", gross: "50–100 cm", sehr_gross: "> 100 cm", komplett: "Fast komplett" };
const ZUSCHLAG_LABELS = { schiebedach: "Zuschlag Schiebedach", panoramadach: "Zuschlag Panoramadach" };

const eur = (n) => Number(n || 0).toLocaleString("de-DE", { minimumFractionDigits: 2 });

export default function AdminSattlerei() {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [settings, setSettings] = useState(null);
  const [detail, setDetail] = useState(null);
  const [notes, setNotes] = useState("");
  const [blockDate, setBlockDate] = useState("");

  const loadAll = async () => {
    try {
      const [b, s] = await Promise.all([
        api.get("/admin/sattlerei/bookings"), api.get("/admin/sattlerei/settings"),
      ]);
      setBookings(b.data); setSettings(s.data);
    } catch (e) {
      if (e?.response?.status === 401) navigate("/admin/login");
      else toast.error("Daten konnten nicht geladen werden");
    }
  };

  useEffect(() => {
    if (!localStorage.getItem("admin_token")) { navigate("/admin/login"); return; }
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/sattlerei/bookings/${id}/status`, { status });
      setBookings((bs) => bs.map((b) => (b.id === id ? { ...b, status } : b)));
      if (detail?.id === id) setDetail({ ...detail, status });
      toast.success("Status aktualisiert");
    } catch { toast.error("Fehler"); }
  };

  const saveNotes = async () => {
    try {
      await api.patch(`/admin/sattlerei/bookings/${detail.id}/notes`, { internal_notes: notes });
      setBookings((bs) => bs.map((b) => (b.id === detail.id ? { ...b, internal_notes: notes } : b)));
      toast.success("Notiz gespeichert");
    } catch { toast.error("Fehler beim Speichern"); }
  };

  const saveSettings = async (patch) => {
    try {
      const { data } = await api.put("/admin/sattlerei/settings", patch);
      setSettings(data);
      toast.success("Gespeichert");
    } catch { toast.error("Fehler beim Speichern"); }
  };

  const savePrice = (group, key, value) => {
    const prices = { ...settings.prices };
    if (key === null) prices[group] = Number(value);
    else prices[group] = { ...prices[group], [key]: Number(value) };
    saveSettings({ prices });
  };

  const openDetail = (b) => { setDetail(b); setNotes(b.internal_notes || ""); };

  const paymentInfo = (b) => {
    if (b.fully_paid) return <span className="text-emerald-600 font-semibold">✓ komplett bezahlt</span>;
    if (b.deposit_paid) return <span className="text-emerald-600 font-semibold">✓ Anzahlung ({eur(b.paid_amount)} €)</span>;
    return <span className="text-slate-400">offen</span>;
  };

  if (!settings) return <div className="min-h-screen flex items-center justify-center text-slate-500">Lade…</div>;

  return (
    <div className="min-h-screen bg-slate-100">
      <header className="bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center gap-4">
          <Link to="/admin" className="text-slate-300 hover:text-white flex items-center gap-1.5 text-sm" data-testid="sattlerei-back-dashboard">
            <ArrowLeft size={16} /> Dashboard
          </Link>
          <div className="font-bold flex items-center gap-2">
            Sattlerei — Verwaltung
            <span className="px-2 py-0.5 rounded-full bg-[#55C96B] text-white text-[10px] font-bold uppercase tracking-widest">Sattlerei</span>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs defaultValue="bookings">
          <TabsList className="bg-white border border-slate-200">
            <TabsTrigger value="bookings" data-testid="sattlerei-tab-bookings">Buchungen ({bookings.length})</TabsTrigger>
            <TabsTrigger value="prices" data-testid="sattlerei-tab-prices">Preise</TabsTrigger>
            <TabsTrigger value="settings" data-testid="sattlerei-tab-settings">Termine & Kapazität</TabsTrigger>
          </TabsList>

          <TabsContent value="bookings" className="mt-5">
            <div className="bg-white rounded-2xl border border-slate-200 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nr.</TableHead><TableHead>Kunde</TableHead><TableHead>Fahrzeug</TableHead>
                    <TableHead>Leistung</TableHead><TableHead>Abgabe / Abholung</TableHead>
                    <TableHead>Preis</TableHead><TableHead>Zahlung</TableHead><TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {bookings.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-slate-400 py-8">Noch keine Buchungen</TableCell></TableRow>}
                  {bookings.map((b) => (
                    <TableRow key={b.id} className="cursor-pointer hover:bg-slate-50/60" onClick={() => openDetail(b)} data-testid={`sattlerei-booking-row-${b.booking_number}`}>
                      <TableCell>
                        <div className="font-mono font-semibold">{b.booking_number}</div>
                        <span className="inline-block mt-1 px-1.5 py-0.5 rounded bg-[#EAF8ED] text-[#249143] text-[10px] font-bold uppercase tracking-wider">Sattlerei</span>
                      </TableCell>
                      <TableCell>
                        <div className="font-medium">{b.first_name} {b.last_name}</div>
                        <div className="text-xs text-slate-500">{b.phone}</div>
                      </TableCell>
                      <TableCell>
                        <div>{b.vehicle?.manufacturer} {b.vehicle?.model}</div>
                        <div className="text-xs text-slate-500">{b.license_plate || "—"}</div>
                      </TableCell>
                      <TableCell className="text-sm">{b.service_label}</TableCell>
                      <TableCell>
                        <div className="font-medium">{b.date} · {b.time}</div>
                        <div className="text-xs text-slate-500">Abholung: {b.pickup_date}</div>
                      </TableCell>
                      <TableCell className="font-semibold">{b.price ? `${eur(b.price)} €` : `ab ${eur(b.price_from)} €`}</TableCell>
                      <TableCell>{paymentInfo(b)}</TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Select value={b.status} onValueChange={(v) => updateStatus(b.id, v)}>
                          <SelectTrigger className="h-9 w-48" data-testid={`sattlerei-status-${b.booking_number}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {Object.entries(STATUS_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="prices" className="mt-5 grid md:grid-cols-2 gap-5 items-start">
            <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-3">
              <h3 className="font-bold text-slate-900">Autohimmel — Festpreise</h3>
              {Object.entries(settings.prices.himmel).map(([k, v]) => (
                <div key={k} className="flex items-center justify-between gap-3">
                  <span className="text-sm font-medium text-slate-700">{HIMMEL_LABELS[k] || k}</span>
                  <Input type="number" defaultValue={v} className="h-10 w-28" onBlur={(e) => savePrice("himmel", k, e.target.value)} data-testid={`sattlerei-price-himmel-${k}`} />
                </div>
              ))}
              <div className="border-t border-slate-100 pt-3 space-y-3">
                {Object.entries(settings.prices.himmel_zuschlag).map(([k, v]) => (
                  <div key={k} className="flex items-center justify-between gap-3">
                    <span className="text-sm font-medium text-slate-700">{ZUSCHLAG_LABELS[k] || k}</span>
                    <Input type="number" defaultValue={v} className="h-10 w-28" onBlur={(e) => savePrice("himmel_zuschlag", k, e.target.value)} data-testid={`sattlerei-price-zuschlag-${k}`} />
                  </div>
                ))}
              </div>
            </div>
            <div className="space-y-5">
              <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-3">
                <h3 className="font-bold text-slate-900">Cabrio-Heckscheibe — Festpreise</h3>
                {Object.entries(settings.prices.heckscheibe).map(([k, v]) => (
                  <div key={k} className="flex items-center justify-between gap-3">
                    <span className="text-sm font-medium text-slate-700">{HECK_LABELS[k] || k}</span>
                    <Input type="number" defaultValue={v} className="h-10 w-28" onBlur={(e) => savePrice("heckscheibe", k, e.target.value)} data-testid={`sattlerei-price-heck-${k}`} />
                  </div>
                ))}
              </div>
              <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-3">
                <h3 className="font-bold text-slate-900">Cabrioverdeck — Preisindikation</h3>
                <div className="flex items-center justify-between gap-3">
                  <span className="text-sm font-medium text-slate-700">Preis „ab" €</span>
                  <Input type="number" defaultValue={settings.prices.verdeck_ab} className="h-10 w-28" onBlur={(e) => savePrice("verdeck_ab", null, e.target.value)} data-testid="sattlerei-price-verdeck" />
                </div>
              </div>
              <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-3">
                <h3 className="font-bold text-slate-900">Fahrersitz reparieren — Preisindikation</h3>
                <div className="flex items-center justify-between gap-3">
                  <span className="text-sm font-medium text-slate-700">Preis „ab" €</span>
                  <Input type="number" defaultValue={settings.prices.fahrersitz_ab} className="h-10 w-28" onBlur={(e) => savePrice("fahrersitz_ab", null, e.target.value)} data-testid="sattlerei-price-fahrersitz" />
                </div>
              </div>
              <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-3">
                <h3 className="font-bold text-slate-900">Sitzwangen reparieren — Preisindikation</h3>
                <div className="flex items-center justify-between gap-3">
                  <span className="text-sm font-medium text-slate-700">Preis „ab" €</span>
                  <Input type="number" defaultValue={settings.prices.sitzwangen_ab} className="h-10 w-28" onBlur={(e) => savePrice("sitzwangen_ab", null, e.target.value)} data-testid="sattlerei-price-sitzwangen" />
                </div>
              </div>
              <p className="text-xs text-slate-500">Änderungen werden beim Verlassen des Feldes gespeichert und gelten sofort für neue Preisberechnungen.</p>
            </div>
          </TabsContent>

          <TabsContent value="settings" className="mt-5">
            <div className="bg-white rounded-2xl border border-slate-200 p-6 max-w-xl space-y-5">
              <div>
                <h3 className="font-bold text-slate-900">Maximale Sattlerei-Fahrzeuge gleichzeitig</h3>
                <Input type="number" min={1} defaultValue={settings.capacity_concurrent} className="h-10 mt-2 w-28" data-testid="sattlerei-capacity-input"
                  onBlur={(e) => saveSettings({ capacity_concurrent: Number(e.target.value) })} />
                <p className="text-xs text-slate-500 mt-1">So viele Fahrzeuge können sich gleichzeitig in der Sattlerei befinden.</p>
              </div>
              <div>
                <h3 className="font-bold text-slate-900">Abgabe-Uhrzeiten (Werktage)</h3>
                <Input defaultValue={(settings.dropoff_slots || []).join(", ")} className="h-10 mt-2" data-testid="sattlerei-slots-input"
                  onBlur={(e) => saveSettings({ dropoff_slots: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} />
                <p className="text-xs text-slate-500 mt-1">Kommagetrennt, z.B. 08:00, 09:00, 10:00, 11:00</p>
              </div>
              <div>
                <h3 className="font-bold text-slate-900">Mindestaufenthalt (Werktage)</h3>
                <Input type="number" min={1} defaultValue={settings.min_workdays} className="h-10 mt-2 w-28" data-testid="sattlerei-minworkdays-input"
                  onBlur={(e) => saveSettings({ min_workdays: Number(e.target.value) })} />
              </div>
              <div>
                <h3 className="font-bold text-slate-900">Gesperrte Tage (Urlaub / Feiertage)</h3>
                <div className="flex gap-2 mt-2">
                  <Input type="date" value={blockDate} onChange={(e) => setBlockDate(e.target.value)} className="h-10" data-testid="sattlerei-block-date" />
                  <Button onClick={() => { if (blockDate) { saveSettings({ blocked_dates: [...(settings.blocked_dates || []), blockDate] }); setBlockDate(""); } }} className="h-10 bg-slate-900" data-testid="sattlerei-block-add">Sperren</Button>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {(settings.blocked_dates || []).map((d) => (
                    <span key={d} className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-red-50 text-red-700 text-sm border border-red-100">
                      {d}
                      <button onClick={() => saveSettings({ blocked_dates: settings.blocked_dates.filter((x) => x !== d) })}><Trash size={13} /></button>
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* DETAIL DIALOG */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="sattlerei-detail-dialog">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  {detail.booking_number}
                  <span className="px-2 py-0.5 rounded-full bg-[#55C96B] text-white text-[10px] font-bold uppercase tracking-widest">Sattlerei</span>
                </DialogTitle>
              </DialogHeader>
              <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                <dt className="text-slate-500">Kunde</dt><dd className="font-medium">{detail.first_name} {detail.last_name}</dd>
                <dt className="text-slate-500">Telefon</dt><dd className="font-medium">{detail.phone}</dd>
                <dt className="text-slate-500">E-Mail</dt><dd className="font-medium break-all">{detail.email}</dd>
                <dt className="text-slate-500">Adresse</dt><dd className="font-medium">{detail.street}, {detail.zip_code} {detail.city}</dd>
                <dt className="text-slate-500">Fahrzeug</dt><dd className="font-medium">{detail.vehicle?.manufacturer} {detail.vehicle?.model} {detail.vehicle?.year && `(${detail.vehicle.year})`}</dd>
                <dt className="text-slate-500">Kennzeichen</dt><dd className="font-medium">{detail.license_plate || "—"}</dd>
                <dt className="text-slate-500">Leistung</dt><dd className="font-medium">{detail.service_label}</dd>
                <dt className="text-slate-500">Schadensart</dt>
                <dd className="font-medium">
                  {[detail.details?.body_class_label, detail.details?.roof_label, detail.details?.zustand_label,
                    detail.details?.rear_window_label, detail.details?.detachment_label,
                    detail.details?.seat_material_label,
                    ...(detail.details?.verdeck_damage_labels || []),
                    ...(detail.details?.seat_damage_labels || [])].filter(Boolean).join(" · ") || "—"}
                </dd>
                <dt className="text-slate-500">Preis</dt><dd className="font-semibold">{detail.price ? `${eur(detail.price)} €` : `ab ${eur(detail.price_from)} € (nach Prüfung)`}</dd>
                <dt className="text-slate-500">Zahlung</dt><dd>{paymentInfo(detail)}</dd>
                {detail.price ? (<><dt className="text-slate-500">Restbetrag</dt><dd className="font-semibold">{eur(detail.price - (detail.paid_amount || 0))} €</dd></>) : null}
                <dt className="text-slate-500">Abgabe</dt><dd className="font-medium">{detail.date} · {detail.time} Uhr</dd>
                <dt className="text-slate-500">Geplante Abholung</dt><dd className="font-medium">{detail.pickup_date}</dd>
                {detail.note && (<><dt className="text-slate-500">Bemerkung Kunde</dt><dd className="font-medium">{detail.note}</dd></>)}
              </dl>

              {(detail.photos || []).length > 0 && (
                <div>
                  <h4 className="font-bold text-slate-900 text-sm mb-2">Fotos ({detail.photos.length})</h4>
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="sattlerei-detail-photos">
                    {detail.photos.map((p) => (
                      <a key={p.id} href={fileUrl(p.storage_path)} target="_blank" rel="noreferrer" className="block aspect-square rounded-xl overflow-hidden border border-slate-200 hover:opacity-90">
                        <img src={fileUrl(p.storage_path)} alt={p.original_filename} className="w-full h-full object-cover" />
                      </a>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <h4 className="font-bold text-slate-900 text-sm mb-2">Status</h4>
                <Select value={detail.status} onValueChange={(v) => updateStatus(detail.id, v)}>
                  <SelectTrigger className="h-10 w-64" data-testid="sattlerei-detail-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(STATUS_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <h4 className="font-bold text-slate-900 text-sm mb-2">Interne Notizen</h4>
                <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} data-testid="sattlerei-detail-notes" />
                <Button onClick={saveNotes} className="mt-2 h-10 bg-slate-900" data-testid="sattlerei-detail-notes-save">Notiz speichern</Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
